import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

// Pre-redesign wireframe — cluttered, dated, low-trust signals.
const BeforeWire = ({ className = "" }) => (
    <svg viewBox="0 0 320 220" className={className} aria-hidden>
        <rect x="1" y="1" width="318" height="218" fill="#f3f3f3" stroke="#ccc" />
        {/* messy header */}
        <rect x="10" y="10" width="60" height="10" fill="#bbb" />
        <rect x="80" y="10" width="40" height="10" fill="#ddd" />
        <rect x="130" y="10" width="50" height="10" fill="#ddd" />
        <rect x="200" y="10" width="40" height="10" fill="#ddd" />
        <rect x="250" y="10" width="60" height="10" fill="#aaa" />
        {/* cluttered body */}
        <rect x="10" y="32" width="200" height="22" fill="#999" />
        <rect x="10" y="60" width="160" height="10" fill="#bbb" />
        <rect x="10" y="74" width="180" height="10" fill="#bbb" />
        <rect x="10" y="88" width="140" height="10" fill="#bbb" />
        <rect x="220" y="32" width="90" height="66" fill="#ddd" />
        {/* lots of color blocks (signaling busy/cheap) */}
        <rect x="10" y="108" width="60" height="30" fill="#888" />
        <rect x="80" y="108" width="60" height="30" fill="#aaa" />
        <rect x="150" y="108" width="60" height="30" fill="#999" />
        <rect x="220" y="108" width="60" height="30" fill="#888" />
        <rect x="10" y="148" width="300" height="14" fill="#ccc" />
        <rect x="10" y="170" width="60" height="14" fill="#999" />
        <rect x="80" y="170" width="60" height="14" fill="#bbb" />
        <rect x="150" y="170" width="60" height="14" fill="#999" />
        <rect x="220" y="170" width="60" height="14" fill="#bbb" />
        <rect x="10" y="194" width="300" height="14" fill="#ddd" />
    </svg>
);

// Post-redesign wireframe — editorial, calm, premium.
const AfterWire = ({ className = "" }) => (
    <svg viewBox="0 0 320 220" className={className} aria-hidden>
        <rect x="1" y="1" width="318" height="218" fill="#ffffff" stroke="#111" />
        {/* minimal header */}
        <text x="14" y="22" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 14, fill: "#111", fontStyle: "italic" }}>
            Pixemance
        </text>
        <rect x="240" y="12" width="68" height="14" fill="#111" />
        {/* hero serif headline lines */}
        <rect x="14" y="44" width="180" height="10" fill="#111" />
        <rect x="14" y="58" width="140" height="10" fill="#111" />
        <rect x="14" y="80" width="200" height="4" fill="#aaa" />
        <rect x="14" y="90" width="180" height="4" fill="#aaa" />
        {/* hero side visual (network) */}
        <g stroke="#111" strokeWidth="0.8" fill="none">
            <circle cx="244" cy="74" r="6" />
            <circle cx="276" cy="92" r="6" />
            <circle cx="244" cy="110" r="6" />
            <line x1="244" y1="74" x2="276" y2="92" />
            <line x1="276" y1="92" x2="244" y2="110" />
        </g>
        {/* divider */}
        <line x1="14" y1="116" x2="306" y2="116" stroke="#ddd" />
        {/* 3 calm pillars */}
        <rect x="14" y="128" width="92" height="60" fill="none" stroke="#ddd" />
        <rect x="114" y="128" width="92" height="60" fill="none" stroke="#ddd" />
        <rect x="214" y="128" width="92" height="60" fill="none" stroke="#ddd" />
        <circle cx="24" cy="140" r="2" fill="#111" />
        <circle cx="124" cy="140" r="2" fill="#111" />
        <circle cx="224" cy="140" r="2" fill="#111" />
        <rect x="22" y="148" width="60" height="3" fill="#111" />
        <rect x="22" y="156" width="40" height="3" fill="#bbb" />
        <rect x="22" y="164" width="50" height="3" fill="#bbb" />
        <rect x="122" y="148" width="60" height="3" fill="#111" />
        <rect x="122" y="156" width="40" height="3" fill="#bbb" />
        <rect x="122" y="164" width="50" height="3" fill="#bbb" />
        <rect x="222" y="148" width="60" height="3" fill="#111" />
        <rect x="222" y="156" width="40" height="3" fill="#bbb" />
        <rect x="222" y="164" width="50" height="3" fill="#bbb" />
        {/* footer */}
        <rect x="14" y="200" width="80" height="6" fill="#111" />
        <rect x="240" y="200" width="66" height="6" fill="#111" />
    </svg>
);

export const CaseStudyVisual = ({ outcome, metrics, label }) => {
    return (
        <div className="relative w-full">
            <div className="grid grid-cols-2 gap-4 md:gap-6">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="relative"
                >
                    <p className="text-[10px] tracking-[0.28em] uppercase text-[#444444] mb-3">
                        Before
                    </p>
                    <div className="border border-[#dddddd] bg-[#fafafa] p-3">
                        <BeforeWire className="w-full h-auto block opacity-90" />
                    </div>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
                    className="relative"
                >
                    <p className="text-[10px] tracking-[0.28em] uppercase text-[#111111] mb-3">
                        After · Pixemance
                    </p>
                    <div className="border border-[#111111] bg-white p-3">
                        <AfterWire className="w-full h-auto block" />
                    </div>
                </motion.div>
            </div>

            {/* Metric strip */}
            {metrics && (
                <div className="mt-6 grid grid-cols-3 border-t border-l border-[#dddddd]">
                    {metrics.map((m, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{
                                duration: 0.7,
                                delay: 0.3 + i * 0.1,
                                ease: EASE,
                            }}
                            className="border-r border-b border-[#dddddd] p-4 bg-white"
                        >
                            <p className="font-serif text-2xl md:text-3xl text-[#111111] leading-none">
                                {m.value}
                            </p>
                            <p className="mt-2 text-[10px] tracking-[0.28em] uppercase text-[#444444]">
                                {m.label}
                            </p>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
};
